/* MinibarReports.jsx — Minibar Raporlar sekmesi.
 * 2 bölüm:
 *  1) Minibar Tüketim Listesi (oda × ürün, tarih aralığı)
 *  2) Misafir Başı Minibar Maliyeti Trendi (haftalık/aylık, mevcut trend kartının kopyası)
 */
import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Loader2, CalendarDays, CalendarRange, UserCheck, ListChecks, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import {
    ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { formatTRNumber } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MinibarReports() {
    const today = new Date().toISOString().slice(0, 10);
    const [dateFrom, setDateFrom] = useState(today);
    const [dateTo, setDateTo] = useState(today);
    const [consData, setConsData] = useState(null);
    const [consLoading, setConsLoading] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfGuestCount, setPdfGuestCount] = useState(0); // rapor için manuel misafir sayısı override

    const [trendPeriod, setTrendPeriod] = useState('weekly');
    const [trendData, setTrendData] = useState(null);
    const [trendLoading, setTrendLoading] = useState(false);

    useEffect(() => { fetchConsumption(); /* eslint-disable-next-line */ }, [dateFrom, dateTo]);
    useEffect(() => { fetchTrend(); /* eslint-disable-next-line */ }, [trendPeriod]);

    const fetchConsumption = async () => {
        setConsLoading(true);
        try {
            const res = await api.get('/minibar/consumption', { params: { date_from: dateFrom, date_to: dateTo } });
            setConsData(res.data);
        } catch {
            setConsData(null);
        } finally {
            setConsLoading(false);
        }
    };

    const fetchTrend = async () => {
        setTrendLoading(true);
        try {
            const res = await api.get('/minibar/cost-trend', { params: { period: trendPeriod } });
            setTrendData(res.data);
        } catch {
            setTrendData(null);
        } finally {
            setTrendLoading(false);
        }
    };

    const downloadPdf = async () => {
        setPdfLoading(true);
        try {
            const token = localStorage.getItem('token');
            // date_from farklıysa aralık toplam misafir sayısı backend tarafında hesaplanır;
            // aksi halde sadece dateTo gününün misafir sayısı otomatik çekilir.
            const params = new URLSearchParams({
                date: dateTo,
                bars: 'minibar',
                minibar_only: 'true',
            });
            if (dateFrom && dateFrom !== dateTo) params.set('date_from', dateFrom);
            if (Number(pdfGuestCount) > 0) params.set('guest_count', String(Number(pdfGuestCount)));
            const res = await fetch(`${API_URL}/api/reports/export/consolidated-pdf?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) {
                toast.error('PDF oluşturulamadı');
                return;
            }
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `minibar_raporu_${dateTo}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            toast.success('Minibar raporu indirildi');
        } catch (e) {
            toast.error('PDF indirme hatası');
        } finally {
            setPdfLoading(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="minibar-reports">
            {/* Günün Durum Sayıları */}
            {consData?.status_counts && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="status-counts">
                    <div className="bg-white border-2 border-slate-300 rounded-lg p-3 text-center">
                        <div className="text-xs text-slate-500 uppercase tracking-wide">Boş</div>
                        <div className="text-2xl font-bold text-slate-700" data-testid="status-bos">{consData.status_counts.bos}</div>
                    </div>
                    <div className="bg-emerald-50 border-2 border-emerald-400 rounded-lg p-3 text-center">
                        <div className="text-xs text-emerald-700 uppercase tracking-wide">Dolu</div>
                        <div className="text-2xl font-bold text-emerald-700" data-testid="status-dolu">{consData.status_counts.dolu}</div>
                    </div>
                    <div className="bg-orange-50 border-2 border-orange-400 rounded-lg p-3 text-center">
                        <div className="text-xs text-orange-700 uppercase tracking-wide">Çıkış</div>
                        <div className="text-2xl font-bold text-orange-700" data-testid="status-cikis">{consData.status_counts.cikis}</div>
                    </div>
                    <div className="bg-red-50 border-2 border-red-500 rounded-lg p-3 text-center">
                        <div className="text-xs text-red-700 uppercase tracking-wide">DND</div>
                        <div className="text-2xl font-bold text-red-600" data-testid="status-dnd">{consData.status_counts.dnd}</div>
                    </div>
                </div>
            )}

            {/* 1) Tüketim Listesi (ürün bazında özet) */}
            <Card>
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <ListChecks className="w-5 h-5 text-emerald-600" /> Minibar Tüketim Özeti
                            </CardTitle>
                            <CardDescription>Tarih aralığında ürün bazında toplam adet ve tutar</CardDescription>
                        </div>
                        <div className="flex items-end gap-2 flex-wrap">
                            <div>
                                <div className="text-xs text-slate-500 mb-1">Başlangıç</div>
                                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" data-testid="cons-date-from" />
                            </div>
                            <div>
                                <div className="text-xs text-slate-500 mb-1">Bitiş</div>
                                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" data-testid="cons-date-to" />
                            </div>
                            <div>
                                <div className="text-xs text-slate-500 mb-1">Misafir Sayısı (PDF)</div>
                                <Input
                                    type="number"
                                    min="0"
                                    value={pdfGuestCount}
                                    onChange={(e) => setPdfGuestCount(e.target.value)}
                                    placeholder="otomatik"
                                    className="h-9 w-28 text-center"
                                    data-testid="pdf-guest-count"
                                />
                            </div>
                            <Button
                                onClick={downloadPdf}
                                disabled={pdfLoading}
                                className="h-9 bg-emerald-600 hover:bg-emerald-700"
                                data-testid="minibar-pdf-btn"
                            >
                                {pdfLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                                PDF İndir
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {consLoading ? (
                        <div className="text-center py-8 text-slate-400">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-1" /> Yükleniyor...
                        </div>
                    ) : !consData || consData.items.length === 0 ? (
                        <div className="text-center py-8 text-slate-400">Bu tarih aralığında veri yok</div>
                    ) : (
                        <div className="overflow-x-auto border rounded-lg">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-100">
                                    <tr>
                                        <th className="text-left p-2 font-medium text-slate-600">Ürün Adı</th>
                                        <th className="text-left p-2 font-medium text-slate-600">Birim</th>
                                        <th className="text-right p-2 font-medium text-slate-600">Adet</th>
                                        <th className="text-right p-2 font-medium text-slate-600">Birim Fiyat (TL)</th>
                                        <th className="text-right p-2 font-medium text-slate-600">Toplam (TL)</th>
                                        <th className="text-right p-2 font-medium text-slate-600">Toplam (EUR)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {consData.items.map((it, i) => (
                                        <tr key={i} className="border-b hover:bg-slate-50" data-testid={`cons-row-${i}`}>
                                            <td className="p-2 font-medium">{it.product_name}</td>
                                            <td className="p-2 text-slate-600">{it.unit}</td>
                                            <td className="p-2 text-right">{formatTRNumber(it.qty, 2)}</td>
                                            <td className="p-2 text-right">{formatTRNumber(it.unit_price_tl, 2)}</td>
                                            <td className="p-2 text-right font-medium">{formatTRNumber(it.total_tl, 2)}</td>
                                            <td className="p-2 text-right text-slate-600">{formatTRNumber(it.total_eur, 2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-emerald-50 border-t-2 border-emerald-300">
                                    <tr>
                                        <td className="p-2 font-bold" colSpan={2}>TOPLAM</td>
                                        <td className="p-2 text-right font-bold" data-testid="cons-total-qty">{formatTRNumber(consData.grand_qty, 2)}</td>
                                        <td></td>
                                        <td className="p-2 text-right font-bold text-emerald-700" data-testid="cons-total-tl">{formatTRNumber(consData.grand_tl, 2)} ₺</td>
                                        <td className="p-2 text-right font-bold text-emerald-700" data-testid="cons-total-eur">{formatTRNumber(consData.grand_eur, 2)} €</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* 2) Misafir Başı Minibar Maliyeti Trendi */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <UserCheck className="w-5 h-5 text-indigo-600" />
                                Misafir Başı Minibar Maliyeti Trendi
                            </CardTitle>
                            <CardDescription>
                                {trendPeriod === 'weekly' ? 'Son 7 günlük' : 'Son 30 günlük'} kişi başı minibar maliyeti
                            </CardDescription>
                        </div>
                        <div className="flex bg-slate-100 rounded-lg p-0.5">
                            <Button
                                variant={trendPeriod === 'weekly' ? 'default' : 'ghost'}
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => setTrendPeriod('weekly')}
                                data-testid="trend-weekly"
                            >
                                <CalendarDays className="w-3.5 h-3.5 mr-1" /> Haftalık
                            </Button>
                            <Button
                                variant={trendPeriod === 'monthly' ? 'default' : 'ghost'}
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => setTrendPeriod('monthly')}
                                data-testid="trend-monthly"
                            >
                                <CalendarRange className="w-3.5 h-3.5 mr-1" /> Aylık
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {trendLoading ? (
                        <div className="text-center py-8 text-slate-400">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-1" /> Yükleniyor...
                        </div>
                    ) : trendData ? (
                        <div className="space-y-4">
                            <div className="bg-indigo-50 rounded-lg p-4 max-w-sm">
                                <div className="text-xs text-indigo-600 mb-1">Ortalama Kişi Başı Minibar Maliyeti (TL)</div>
                                <p className="text-2xl font-bold text-indigo-800" data-testid="trend-avg">
                                    {trendData.avg_per_person_tl?.toFixed(2) || '0.00'} TL
                                </p>
                                <p className="text-xs text-indigo-500 mt-1">
                                    {trendData.days_with_data || 0} gün verisi mevcut
                                </p>
                            </div>

                            {trendData.trend.filter(t => t.per_person_tl > 0).length === 0 ? (
                                <div className="text-center text-slate-400 py-8">
                                    <UserCheck className="w-10 h-10 mx-auto mb-1 opacity-50" />
                                    <p>Misafir sayısı + minibar girişi olan gün bulunamadı</p>
                                </div>
                            ) : (
                                <>
                                    <div className="w-full h-72 bg-gradient-to-br from-indigo-50/40 to-white rounded-lg p-3 border border-indigo-100">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <ComposedChart data={trendData.trend} margin={{ top: 16, right: 24, left: 0, bottom: 0 }}>
                                                <defs>
                                                    <linearGradient id="mbTotal" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.85} />
                                                        <stop offset="100%" stopColor="#10b981" stopOpacity={0.45} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} />
                                                <YAxis yAxisId="left" tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v} tick={{ fontSize: 11, fill: '#64748b' }} />
                                                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#6366f1' }} />
                                                <Tooltip
                                                    contentStyle={{ background: '#0f172a', border: 'none', borderRadius: 8, color: '#f1f5f9', fontSize: 12 }}
                                                    formatter={(v, name) => {
                                                        if (name === 'Toplam (TL)') return [`${Number(v).toLocaleString('tr-TR')} TL`, name];
                                                        if (name === 'Kişi Başı (TL)') return [`${Number(v).toFixed(2)} TL/kişi`, name];
                                                        if (name === 'Misafir') return [`${Number(v).toLocaleString('tr-TR')} kişi`, name];
                                                        return [v, name];
                                                    }}
                                                />
                                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                                <Bar yAxisId="left" dataKey="total_tl" name="Toplam (TL)" fill="url(#mbTotal)" radius={[6, 6, 0, 0]} />
                                                <Line yAxisId="right" type="monotone" dataKey="per_person_tl" name="Kişi Başı (TL)" stroke="#6366f1" strokeWidth={3} dot={{ r: 4, fill: '#6366f1' }} activeDot={{ r: 6 }} />
                                                <Line yAxisId="right" type="monotone" dataKey="guest_count" name="Misafir" stroke="#f59e0b" strokeDasharray="4 4" strokeWidth={2} dot={false} />
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="border-b bg-slate-50">
                                                    <th className="text-left p-2 font-medium text-slate-600">Tarih</th>
                                                    <th className="text-right p-2 font-medium text-slate-600">Misafir</th>
                                                    <th className="text-right p-2 font-medium text-slate-600">Toplam (TL)</th>
                                                    <th className="text-right p-2 font-medium text-slate-600">Kişi Başı (TL)</th>
                                                    <th className="text-right p-2 font-medium text-slate-600">Kişi Başı (EUR)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {trendData.trend.filter(t => t.total_tl > 0 || t.guest_count > 0).map((t) => (
                                                    <tr key={t.date} className="border-b hover:bg-slate-50">
                                                        <td className="p-2 text-slate-700">{t.label}</td>
                                                        <td className="p-2 text-right">
                                                            {t.guest_count > 0 ? <Badge variant="outline" className="text-xs">{t.guest_count}</Badge> : <span className="text-slate-300">-</span>}
                                                        </td>
                                                        <td className="p-2 text-right">{formatTRNumber(t.total_tl, 2)}</td>
                                                        <td className="p-2 text-right font-medium">{t.per_person_tl > 0 ? formatTRNumber(t.per_person_tl, 2) : '-'}</td>
                                                        <td className="p-2 text-right text-slate-600">{t.per_person_eur > 0 ? formatTRNumber(t.per_person_eur, 2) : '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-8 text-slate-400">Veri yüklenemedi</div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
